/**
 * Wire + storage shapes for FastAPI token auth.
 *
 * COPIED VERBATIM from `frontend/mobile/src/lib/auth/types.ts`; only this header and
 * the "device" wording differ. The wire contract is shared, so the two files
 * must stay identical in substance.
 *
 * The contract is `backend/api/app/schemas/mobile_auth.py` (serialised by alias, so
 * every JSON key is camelCase) and `backend/api/tests/test_mobile_auth_routes.py`.
 */

export type UserRole = 'ADMIN' | 'USER';

export interface MobileUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  rentModuleAccess: boolean;
  /**
   * Wave 3 (agent W12, auth hardening) — set when the account must change its
   * password before doing anything else (e.g. an admin-forced reset). This
   * field only SURFACES the flag on the type; enforcing the full-screen gate
   * (`ForcePasswordChangePage`) at the route level is not this file's job —
   * see that page's header for the recommended wiring.
   */
  mustChangePassword?: boolean;
}

/** `MobileTokenPair` — the body of a successful login/refresh. */
export interface MobileTokenPair {
  accessToken: string;
  tokenType: string;
  /** Access token lifetime in SECONDS (900 = 15 min). */
  expiresIn: number;
  refreshToken: string;
  /** ISO-8601 with milliseconds and a trailing `Z`. */
  refreshTokenExpiresAt: string;
  user: MobileUser;
}

/**
 * What we keep in browser storage. One object, one secure-store key — see
 * `session.ts` for why the whole thing is written in a single operation.
 */
export interface StoredSession {
  accessToken: string;
  /** Epoch ms. Derived from `expiresIn` at the moment of issue. */
  accessTokenExpiresAt: number;
  refreshToken: string;
  /** Epoch ms, parsed from `refreshTokenExpiresAt`. */
  refreshTokenExpiresAt: number;
  user: MobileUser;
}

export type AuthErrorCode =
  | 'validation'
  | 'invalid_credentials'
  | 'refresh_failed'
  | 'no_session'
  | 'persist_failed'
  | 'network'
  | 'server'
  /* --- Google sign-in (V5-09/V5-10) ------------------------------------- */
  /** The ID token did not verify. Opaque on purpose: the server does not say
   *  which of signature / audience / expiry / email_verified failed. */
  | 'invalid_token'
  /** Google verified the person, but AIM has never seen this email. An
   *  `AccessRequest` was queued; the UI routes them to the pending screen
   *  rather than pretending sign-in failed. */
  | 'access_request_pending'
  /** A known account with `isActive = false`. A valid Google identity does not
   *  override AIM's own decision to deactivate someone. */
  | 'account_disabled'
  /** This deployment has Google sign-in switched off. */
  | 'google_disabled'
  /* --- MFA (Wave 3, agent W12) ------------------------------------------- */
  /** `POST /login` answered 200 with `{ mfaRequired: true, mfaToken }`
   *  instead of a token pair — the password was right, but a second factor
   *  is enrolled. `mfaToken` on the error carries the short-lived token the
   *  code-entry step must present to `POST /auth/mfa`. */
  | 'mfa_required';

/** Every failure this layer raises. `message` is safe to show to the user. */
export class AuthError extends Error {
  readonly code: AuthErrorCode;
  readonly status: number | null;
  /**
   * The address Google verified, on the two outcomes that have one.
   *
   * Safe to display: it is echoed only after the person proved they control
   * that mailbox, and only back to them. It is what lets the pending screen
   * say WHICH account is waiting for approval.
   */
  readonly email: string | null;
  /** Set only on `mfa_required` — the token the code-entry step must echo
   *  back to `POST /auth/mfa` alongside the 6-digit (or recovery) code. */
  readonly mfaToken: string | null;

  constructor(
    message: string,
    code: AuthErrorCode,
    status: number | null = null,
    email: string | null = null,
    mfaToken: string | null = null,
  ) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.status = status;
    this.email = email;
    this.mfaToken = mfaToken;
  }
}
